import axios from "axios";
import { headers } from "../global_vars";

const ADD_DOCUMENT = "https://ultima.icsgr.com/api/data_management/all_documents/";

export const ACCEPTED_DOCUMENT_EXTENSIONS = [
  "pdf",
  "xlsx",
  "xlsm",
  "xls",
  "docx",
  "doc",
  "tif",
  "tiff",
  "bmp",
  "jpg",
  "jpeg",
  "png",
  "csv",
  "dot",
  "dotx",
  "mp3",
  "mp4",
  "pptx",
  "zip",
  "rar",
  "txt",
  "wav",
  "flv",
];

/** For file picker `accept` attribute. */
export const ACCEPTED_DOCUMENT_ACCEPT = ACCEPTED_DOCUMENT_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",");

const ACCEPTED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-word.document.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "image/jpeg",
  "image/png",
  "image/tiff",
  "image/bmp",
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "video/mp4",
  "video/x-flv",
  "application/zip",
  "application/x-zip-compressed",
  "application/vnd.rar",
  "application/x-rar-compressed",
]);

export function titleFromFilename(filename) {
  if (!filename) return "Document";

  const base = filename.replace(/^.*[\\/]/, "");
  const lastDot = base.lastIndexOf(".");
  let title = (lastDot > 0 ? base.slice(0, lastDot) : base).trim();

  if (title.length < 2) {
    title = base.trim();
  }
  if (title.length < 2) {
    title = "Document";
  }

  return title.slice(0, 40);
}

export function normalizeIncomingFiles(incoming) {
  if (!incoming) return [];
  if (incoming instanceof File) return [incoming];
  if (Array.isArray(incoming)) return incoming.filter((f) => f instanceof File);
  if (typeof incoming.length === "number") {
    return Array.from(incoming).filter((f) => f instanceof File);
  }
  return [];
}

export function isAcceptedDocumentFile(file) {
  if (!file) return false;

  const name = file.name || "";
  const ext = name.includes(".") ? name.split(".").pop()?.toLowerCase() : "";
  if (ext && ACCEPTED_DOCUMENT_EXTENSIONS.includes(ext)) {
    return true;
  }

  if (file.type && ACCEPTED_MIME_TYPES.has(file.type)) {
    return true;
  }

  return false;
}

export async function uploadDocumentQuick({ file, projectId, clientId, categoryId }) {
  const formData = new FormData();
  formData.append("title", titleFromFilename(file.name));
  if (projectId) formData.append("project_id", projectId);
  if (clientId) formData.append("client_id", clientId);
  if (categoryId) formData.append("category_id", categoryId);
  formData.append("original", "false");
  formData.append("trafficable", "true");
  formData.append("filepath", file);

  const currentHeaders = {
    ...headers,
    Authorization: "Token " + localStorage.getItem("userToken"),
  };
  delete currentHeaders["Content-Type"];

  return axios.post(ADD_DOCUMENT, formData, { headers: currentHeaders });
}

function humanizeErrorPayload(payload) {
  if (payload == null) return null;
  if (typeof payload === "string") {
    const uniqueId = payload.match(/Document with this Document id already exists/i);
    if (uniqueId) {
      return "Could not assign a unique document ID. Please try again.";
    }
    return payload;
  }
  if (Array.isArray(payload)) {
    return payload.map(humanizeErrorPayload).filter(Boolean).join("\n");
  }
  if (typeof payload === "object") {
    return Object.entries(payload)
      .map(([field, messages]) => {
        const text = humanizeErrorPayload(messages);
        if (field === "error" || field === "detail") return text;
        return `${field}: ${text}`;
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(payload);
}

export function formatUploadError(error) {
  const formatted = humanizeErrorPayload(error.response?.data);
  if (formatted) return formatted;
  return "An error occurred while uploading the document.";
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
